import { useEffect, useState } from "react";
import { Users, School as SchoolIcon, Trophy, Activity, AlertTriangle } from "lucide-react";
import AdminStatCard from "@/components/AdminStatCard";
import { supabase } from "@/lib/supabaseClient";

interface DashboardSnapshot {
  totalStudents: number;
  totalSchools: number;
  totalGames: number;
  avgScore: number | null;
  topSchool: { name: string; avg: number } | null;
  recent: { full_name: string; school_name: string | null; score: number; completed_at: string }[];
  schoolsMissingCoords: number;
}

export default function AdminDashboardPage() {
  const [snap, setSnap] = useState<DashboardSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [students, schools, gamesAll, recent, missing] = await Promise.all([
          supabase.from("students").select("id", { count: "exact", head: true }),
          supabase.from("schools").select("id", { count: "exact", head: true }),
          supabase.from("game_sessions").select("id, score, status", { count: "exact" }).eq("status", "completed"),
          supabase.from("leaderboard_view").select("full_name, school_name, best_score, completed_at").order("completed_at", { ascending: false }).limit(8),
          supabase.from("schools").select("id", { count: "exact", head: true }).is("latitude", null),
        ]);

        const scores = (gamesAll.data ?? []).map((g) => Number(g.score));
        const avg = scores.length ? scores.reduce((s, x) => s + x, 0) / scores.length : null;

        // Top school by avg score
        const { data: bySchool } = await supabase
          .from("leaderboard_view")
          .select("school_name, best_score")
          .not("school_name", "is", null);
        const grouped = new Map<string, { sum: number; n: number }>();
        (bySchool ?? []).forEach((r) => {
          const k = r.school_name as string;
          const prev = grouped.get(k) ?? { sum: 0, n: 0 };
          grouped.set(k, { sum: prev.sum + Number(r.best_score), n: prev.n + 1 });
        });
        let topSchool: DashboardSnapshot["topSchool"] = null;
        for (const [name, v] of grouped) {
          const a = v.sum / v.n;
          if (!topSchool || a > topSchool.avg) topSchool = { name, avg: a };
        }

        setSnap({
          totalStudents: students.count ?? 0,
          totalSchools: schools.count ?? 0,
          totalGames: gamesAll.count ?? 0,
          avgScore: avg,
          topSchool,
          recent: (recent.data ?? []).map((r) => ({
            full_name: r.full_name,
            school_name: r.school_name,
            score: Number(r.best_score),
            completed_at: r.completed_at as string,
          })),
          schoolsMissingCoords: missing.count ?? 0,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load dashboard.");
      }
    }
    void load();
  }, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl text-white">Manager dashboard</h1>
        <p className="text-steel-300">Manage question banks, student performance, schools, and the statewide leaderboard.</p>
      </header>

      {error && <div className="pill-bad px-3 py-2">{error}</div>}

      {snap?.schoolsMissingCoords ? (
        <div className="pill-warn px-3 py-2 inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {snap.schoolsMissingCoords} school(s) missing latitude/longitude — won't appear on the map.
        </div>
      ) : null}

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <AdminStatCard label="Students" value={snap?.totalStudents ?? "—"} icon={<Users className="w-5 h-5 text-forge-400" />} />
        <AdminStatCard label="Schools"  value={snap?.totalSchools ?? "—"}  icon={<SchoolIcon className="w-5 h-5 text-forge-400" />} />
        <AdminStatCard label="Games completed" value={snap?.totalGames ?? "—"} icon={<Trophy className="w-5 h-5 text-forge-400" />} />
        <AdminStatCard label="Avg score" value={snap?.avgScore != null ? Math.round(snap.avgScore) : "—"} icon={<Activity className="w-5 h-5 text-forge-400" />} />
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <div className="card-dark p-5">
          <h2 className="font-display text-lg text-white mb-3">Top school</h2>
          {snap?.topSchool ? (
            <>
              <div className="text-2xl font-display">{snap.topSchool.name}</div>
              <div className="text-steel-300 text-sm">avg score · {Math.round(snap.topSchool.avg)}</div>
            </>
          ) : <div className="text-steel-400">No data yet.</div>}
        </div>
        <div className="card-dark p-5">
          <h2 className="font-display text-lg text-white mb-3">Recent attempts</h2>
          <ul className="divide-y divide-steel-700 text-sm">
            {(snap?.recent ?? []).map((r, i) => (
              <li key={i} className="py-2 flex justify-between">
                <span>{r.full_name} <span className="text-steel-400">· {r.school_name ?? "—"}</span></span>
                <span className="text-forge-300 font-display">{r.score}</span>
              </li>
            ))}
            {snap?.recent.length === 0 && <li className="py-2 text-steel-400">Nothing yet.</li>}
          </ul>
        </div>
      </section>
    </div>
  );
}
